import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Eye, EyeOff, Info } from "lucide-react";
import AuthShell from "../../components/AuthShell";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { useAuth } from "../../auth/AuthProvider";
import { errorMessage } from "../../lib/api";

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const next = searchParams.get("next") || "/";
  const { signIn } = useAuth();
  const { t } = useTranslation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // UX-23: при истёкшей сессии RequireAuth редиректит сюда с ?next=…, но
  // пользователь не понимал, почему его выкинуло. Показываем причину.
  const sessionExpired = searchParams.get("next") !== null;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await signIn({ email: email.trim(), password });
      navigate(decodeURIComponent(next), { replace: true });
    } catch (err) {
      setError(errorMessage(err, t("common.error")));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell
      title={t("auth.login")}
      subtitle="Войдите, чтобы продолжить работу с документами и процессами."
      footer={
        <span>
          {t("auth.no_account")}{" "}
          <Link
            to="/signup"
            className="text-primary hover:underline"
            data-testid="link-signup"
          >
            {t("auth.create_account")}
          </Link>
        </span>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
        {sessionExpired && !error && (
          <div
            data-testid="login-session-expired"
            className="flex items-start gap-2 text-sm text-zinc-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2"
          >
            <Info className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />
            <span>Сессия истекла — войдите снова, и мы вернём вас на прежнюю страницу.</span>
          </div>
        )}
        <div className="space-y-1.5">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            required
            data-testid="login-email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="password">{t("common.password")}</Label>
          {/* UX-4: без переключателя видимости опечатка в пароле неотличима от
              неверного пароля. */}
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              required
              data-testid="login-password"
              className="pr-10"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              data-testid="login-password-toggle"
              aria-label={showPassword ? "Скрыть пароль" : "Показать пароль"}
              aria-pressed={showPassword}
              className="absolute inset-y-0 right-0 flex items-center px-3 text-zinc-500 hover:text-zinc-800"
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>

        {/* UX-2: role="alert" — без него скринридер не сообщал об ошибке входа. */}
        {error && (
          <div
            data-testid="login-error"
            role="alert"
            className="text-sm text-destructive-strong bg-destructive/10 rounded-md px-3 py-2"
          >
            {error}
          </div>
        )}

        <Button
          type="submit"
          disabled={submitting}
          data-testid="login-submit"
          className="w-full rounded-full"
        >
          {submitting ? t("auth.logging_in") : t("auth.login")}
        </Button>

        <div className="text-sm text-zinc-500 text-center">
          <Link
            to="/forgot-password"
            className="hover:underline"
            data-testid="link-forgot"
          >
            Забыли пароль?
          </Link>
        </div>

        <div className="relative py-1">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t border-zinc-200" />
          </div>
          <div className="relative flex justify-center">
            <span className="bg-white px-2 text-xs text-zinc-400">{t("auth.ecp_or")}</span>
          </div>
        </div>
        <Link
          to={`/ecp${next && next !== "/" ? `?next=${encodeURIComponent(next)}` : ""}`}
          data-testid="link-ecp"
          className="block w-full text-center rounded-full border border-zinc-300 px-4 py-2 text-sm font-medium hover:bg-zinc-50"
        >
          {t("auth.ecp_login_title")}
        </Link>
      </form>
    </AuthShell>
  );
}
